// Export & Share utilities for Selah Downloads
// Provides:
//   • formatPlaintext(entry) -> formatted string representation
//   • copyAsText(entry)      -> copies formatted text to clipboard
//   • exportAsPDF(entry)     -> generates and downloads a branded PDF
//   • sharePlatform(entry)   -> uses Web Share API with graceful fallbacks
//
// An "entry" is expected to look like:
//   {
//     downloadText: string (decrypted original text),
//     analysis: {
//       category: string,
//       themes: string[],
//       insights: string[],
//       verse: { text, reference, translation },
//       prayer: { paragraph1, paragraph2 }
//     },
//     createdAt?: string | Date   (optional, falls back to now)
//   }

import jsPDF from 'jspdf';

const BRAND = {
  primary: [184, 120, 132], // rose-gold
  primaryDark: [167, 99, 115],
  text: [44, 37, 39], // warm brown-black
  textSecondary: [92, 79, 82],
  muted: [138, 124, 127],
  surface: [250, 247, 246],
  accent: [201, 148, 157],
};

const safeDate = (d) => {
  try {
    const dt = d ? new Date(d) : new Date();
    return isNaN(dt.getTime()) ? new Date() : dt;
  } catch (_) {
    return new Date();
  }
};

const formatDateLong = (d) =>
  safeDate(d).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

// ---------------------------------------------------------------------------
// Plaintext formatting
// ---------------------------------------------------------------------------
export const formatPlaintext = (entry) => {
  const { downloadText = '', analysis = {}, createdAt } = entry || {};
  const {
    category = '',
    themes = [],
    insights = [],
    verse = {},
    prayer = {},
  } = analysis || {};

  const lines = [];
  lines.push('SELAH DOWNLOADS');
  lines.push('Your Spiritual Insight');
  lines.push(formatDateLong(createdAt));
  if (category) lines.push(`Category: ${category}`);
  if (themes && themes.length) lines.push(`Themes: ${themes.join(', ')}`);
  lines.push('');
  lines.push('—'.repeat(40));
  lines.push('');

  if (downloadText) {
    lines.push('YOUR DOWNLOAD');
    lines.push(downloadText.trim());
    lines.push('');
  }

  if (insights && insights.length) {
    lines.push('3 INSIGHTS FOR YOU');
    insights.forEach((ins, i) => {
      lines.push(`${i + 1}. ${ins}`);
      lines.push('');
    });
  }

  if (verse && (verse.text || verse.reference)) {
    lines.push('APPLICABLE SCRIPTURE');
    if (verse.text) lines.push(`"${verse.text}"`);
    const ref = [verse.reference, verse.translation ? `(${verse.translation})` : '']
      .filter(Boolean)
      .join(' ');
    if (ref) lines.push(`— ${ref}`);
    lines.push('');
  }

  if (prayer && (prayer.paragraph1 || prayer.paragraph2)) {
    lines.push('YOUR ACTIVATION PRAYER');
    if (prayer.paragraph1) lines.push(prayer.paragraph1);
    if (prayer.paragraph2) {
      lines.push('');
      lines.push(prayer.paragraph2);
    }
    lines.push('');
    lines.push('— Amen');
    lines.push('');
  }

  lines.push('—'.repeat(40));
  lines.push('Pause. Listen. Receive.');
  lines.push('selah-downloads.app');

  return lines.join('\n');
};

// ---------------------------------------------------------------------------
// Copy to clipboard
// ---------------------------------------------------------------------------
export const copyAsText = async (entry) => {
  const text = formatPlaintext(entry);

  // Try modern async clipboard API first
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (_err) {
      // Permission denied or other runtime failure; fall through to legacy path.
    }
  }

  // Legacy fallback: hidden textarea + execCommand('copy')
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '0';
    ta.style.left = '-9999px';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    if (ok) return true;
  } catch (_err) {
    /* fall through */
  }

  throw new Error(
    'Clipboard access is blocked. Please copy the text manually or grant clipboard permission.'
  );
};

// ---------------------------------------------------------------------------
// PDF export
// ---------------------------------------------------------------------------
export const buildPDF = (entry) => {
  const { downloadText = '', analysis = {}, createdAt } = entry || {};
  const {
    category = '',
    themes = [],
    insights = [],
    verse = {},
    prayer = {},
  } = analysis || {};

  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 56; // ~0.78"
  const contentWidth = pageWidth - margin * 2;

  let cursorY = margin;

  // Background wash on the header
  doc.setFillColor(...BRAND.surface);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');

  // Header band
  doc.setFillColor(...BRAND.primary);
  doc.rect(0, 0, pageWidth, 6, 'F');

  // Brand title
  doc.setTextColor(...BRAND.primaryDark);
  doc.setFont('times', 'bold');
  doc.setFontSize(26);
  doc.text('Selah Downloads', margin, cursorY + 18);

  // Tagline
  doc.setFont('times', 'italic');
  doc.setFontSize(11);
  doc.setTextColor(...BRAND.muted);
  doc.text('Pause. Listen. Receive.', margin, cursorY + 36);

  cursorY += 62;

  // Date
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...BRAND.textSecondary);
  doc.text(formatDateLong(createdAt), margin, cursorY);
  cursorY += 18;

  // Category badge
  if (category) {
    const label = category.toUpperCase();
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    const w = doc.getTextWidth(label) + 16;
    doc.setFillColor(255, 232, 236);
    doc.setDrawColor(...BRAND.primary);
    doc.roundedRect(margin, cursorY - 10, w, 16, 8, 8, 'FD');
    doc.setTextColor(...BRAND.primaryDark);
    doc.text(label, margin + 8, cursorY + 1);
    cursorY += 18;
  }

  // Themes
  if (themes && themes.length) {
    cursorY += 4;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(...BRAND.textSecondary);
    let x = margin;
    const tagY = cursorY;
    themes.forEach((t) => {
      const tw = doc.getTextWidth(t) + 14;
      if (x + tw > pageWidth - margin) {
        x = margin;
        cursorY += 22;
      }
      doc.setFillColor(252, 242, 244);
      doc.setDrawColor(...BRAND.accent);
      doc.roundedRect(x, cursorY - 10, tw, 16, 8, 8, 'FD');
      doc.setTextColor(106, 58, 68);
      doc.text(t, x + 7, cursorY + 1);
      x += tw + 6;
    });
    cursorY = tagY + 20;
  }

  cursorY += 14;

  // Helper: section heading
  const drawHeading = (heading) => {
    if (cursorY > pageHeight - margin - 60) {
      doc.addPage();
      cursorY = margin;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...BRAND.primaryDark);
    doc.text(heading.toUpperCase(), margin, cursorY);
    cursorY += 6;
    doc.setDrawColor(...BRAND.accent);
    doc.setLineWidth(0.6);
    doc.line(margin, cursorY, margin + 36, cursorY);
    cursorY += 14;
  };

  // Helper: paragraph (wrapped text)
  const drawParagraph = (text, opts = {}) => {
    const {
      font = 'helvetica',
      style = 'normal',
      size = 11,
      color = BRAND.text,
      lineSpacing = 1.4,
      indentX = 0,
      bullet = null,
    } = opts;
    doc.setFont(font, style);
    doc.setFontSize(size);
    doc.setTextColor(...color);
    const wrapped = doc.splitTextToSize(String(text || ''), contentWidth - indentX);
    const lineHeight = size * lineSpacing;
    let first = true;
    wrapped.forEach((line) => {
      if (cursorY > pageHeight - margin - 20) {
        doc.addPage();
        cursorY = margin;
      }
      if (first && bullet) {
        const bulletX = margin;
        doc.setFillColor(...BRAND.primary);
        doc.circle(bulletX + 8, cursorY - 3, 8, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.setTextColor(255, 255, 255);
        doc.text(String(bullet), bulletX + 8, cursorY, {
          align: 'center',
          baseline: 'middle',
        });
        doc.setFont(font, style);
        doc.setFontSize(size);
        doc.setTextColor(...color);
      }
      doc.text(line, margin + indentX, cursorY);
      cursorY += lineHeight;
      first = false;
    });
  };

  // Your download
  if (downloadText) {
    drawHeading('Your Download');
    drawParagraph(downloadText);
    cursorY += 10;
  }

  // Insights
  if (insights && insights.length) {
    drawHeading('3 Insights For You');
    insights.forEach((ins, i) => {
      drawParagraph(ins, { indentX: 28, bullet: i + 1 });
      cursorY += 8;
    });
  }

  // Scripture
  if (verse && (verse.text || verse.reference)) {
    drawHeading('Applicable Scripture');
    if (verse.text) {
      drawParagraph(`"${verse.text}"`, {
        font: 'times',
        style: 'italic',
        size: 13,
        lineSpacing: 1.5,
      });
    }
    const ref = [verse.reference, verse.translation ? `(${verse.translation})` : '']
      .filter(Boolean)
      .join(' ');
    if (ref) {
      cursorY += 4;
      drawParagraph(`— ${ref}`, {
        font: 'times',
        style: 'bold',
        size: 11,
        color: BRAND.primaryDark,
      });
    }
    cursorY += 10;
  }

  // Prayer
  if (prayer && (prayer.paragraph1 || prayer.paragraph2)) {
    drawHeading('Your Activation Prayer');
    if (prayer.paragraph1) {
      drawParagraph(prayer.paragraph1);
      cursorY += 8;
    }
    if (prayer.paragraph2) {
      drawParagraph(prayer.paragraph2);
      cursorY += 8;
    }
    drawParagraph('— Amen', {
      style: 'italic',
      color: BRAND.muted,
    });
  }

  // Footer on every page
  const pageCount = doc.getNumberOfPages();
  for (let p = 1; p <= pageCount; p += 1) {
    doc.setPage(p);
    doc.setDrawColor(...BRAND.accent);
    doc.setLineWidth(0.4);
    doc.line(margin, pageHeight - 40, pageWidth - margin, pageHeight - 40);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...BRAND.muted);
    doc.text('Selah Downloads — Pause. Listen. Receive.', margin, pageHeight - 26);
    doc.text(`${p} / ${pageCount}`, pageWidth - margin, pageHeight - 26, {
      align: 'right',
    });
  }

  return doc;
};

export const exportAsPDF = (entry) => {
  const doc = buildPDF(entry);
  const stamp = safeDate(entry?.createdAt).toISOString().slice(0, 10);
  const cat = (entry?.analysis?.category || 'download')
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  const filename = `selah-${cat}-${stamp}.pdf`;

  // Build a Blob and trigger a manual download.
  // This approach works in strict contexts (iframes, sandboxes) where
  // jsPDF's built-in doc.save() sometimes silently fails.
  try {
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      if (a.parentNode) document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 250);
    return filename;
  } catch (err) {
    // Last resort — open the PDF in a new tab so the user can save it manually.
    try {
      const dataUrl = doc.output('datauristring');
      const w = window.open(dataUrl, '_blank', 'noopener,noreferrer');
      if (!w) {
        throw new Error(
          "Couldn't start the download. Please allow popups or try again."
        );
      }
      return filename;
    } catch (_e) {
      throw err;
    }
  }
};

// ---------------------------------------------------------------------------
// Native Web Share
// ---------------------------------------------------------------------------
export const canNativeShare = () => typeof navigator !== 'undefined' && !!navigator.share;

export const sharePlatform = async (entry) => {
  const text = formatPlaintext(entry);
  const title = 'My Selah Download';

  if (canNativeShare()) {
    try {
      await navigator.share({ title, text });
      return { ok: true, method: 'native' };
    } catch (err) {
      // User cancelled or denied
      if (err && (err.name === 'AbortError' || err.code === 20)) {
        return { ok: false, method: 'native', reason: 'cancelled' };
      }
      // Fall through to clipboard
    }
  }

  await copyAsText(entry);
  return { ok: true, method: 'clipboard-fallback' };
};
